// 가입(Register.js) 전용 — 외주 수주 자격 번호의 메타 쓰기와 「외주 수주 의무 확인」 서약 기록 (S5, 2026-10-27)
//   계획 docs/계획_가입위주_외주업체등록_S5_2026-10-27.md §2-B · 명세 §3-1·§3-2·§4-1 · 법무 §1-4(서약 저장 구조)·§3-2(번호 금지 목록)·§10 L9.
//   - 번호(치과기공사 면허·개설등록·제조업 허가)는 users 가 아니라 vendorDocs/{uid}/files 메타에만 둔다(users 는 전 업체 회원이 읽음 — rules:142-146).
//   - 메타 쓰기 실패는 여기서 삼키지 않고 호출자에게 { ok:false, failedTypes } 로 돌려준다 → Register.js 가 users.vendorIntent.metaFailed:true 로 표식(가입은 완료).
//     ★ 실패했다고 번호를 users 에 대신 저장하는 fallback 금지(법무 L9). 콘솔·alert 에 번호 문자열 금지(법무 §3-2) — 아래 로그는 type 만 찍는다.
//   - outsourceMfApi.js 는 다른 작업자가 수정 중이라 import 만 한다(saveVendorLicense 재사용).
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { saveVendorLicense } from './outsourceMfApi';

// vendorDocs 메타 type — 번호만 담는 메타(path 없음). rules vendorDocs create 에 number 분기는 서버 작업자(명세 §4-4 ②) — 배포 전까지는 permission-denied 로 실패 → metaFailed 표식
export const VENDOR_NUMBER_META_TYPES = Object.freeze({ mfg_permit: 'mfg_permit', opening: 'opening' });
export const VENDOR_NUMBER_MAX = 30;      // 개설등록·제조업 허가번호 길이 상한(rules 와 같음)
export const VENDOR_LICENSE_MAX = 20;     // 면허번호·성명 길이 상한(saveVendorLicense 와 같음)

// ── 서약(「외주 수주 의무 확인」) — 법무 §1-4 문안 글자 그대로 ──
//   v8.0 시행 전(가입 시 약관 판 v6.2/v7.0 — 조문 없음): 독립 약정 문안 + 판·해시 기록 / v8.0 시행 후: 조문 인용 라벨. 분기는 isTermsV80Effective(features)(config/termsVersions.js:49).
export const VENDOR_PLEDGE_LABEL_TRANSITIONAL = '외주 수주 의무를 확인했습니다 (필수) — 덴클로 받은 의뢰를 다른 곳에 다시 맡기지 않고(재재위탁 금지), 결과물은 의뢰한 기공소에만 보내며(치과·환자 직접 발송 금지), 받은 파일은 작업 완료 후 지체 없이 파기합니다. 치과기공소 개설등록·치과기공사 면허(제조업체는 의료기기 제조업 허가)를 갖추지 못하거나 잃으면 외주 수주를 할 수 없습니다. 이 확인은 이용약관 개정(제4조의10·제4조의10의2) 시행 후 그 조문으로 대체됩니다.';
export const VENDOR_PLEDGE_LABEL_V80 = '이용약관 제4조의10·제4조의10의2의 외주 수주 의무를 확인했습니다';
export const VENDOR_PLEDGE_VERSION_TRANSITIONAL = 'pledge-2026-10-v1';
export const VENDOR_PLEDGE_VERSION_V80 = 'terms-v8.0';

export function vendorPledgeLabel(termsV80Live) {
    return termsV80Live ? VENDOR_PLEDGE_LABEL_V80 : VENDOR_PLEDGE_LABEL_TRANSITIONAL;
}

/** 라벨 SHA-256 hex (Web Crypto). 계산 불가 환경이면 null — 호출자가 textHashFailed 로 표식(조용한 실패 금지) */
export async function sha256Hex(text) {
    try {
        const subtle = (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) ? window.crypto.subtle : null;
        if (!subtle) { console.error('[가입] Web Crypto 사용 불가 — 서약 문안 해시를 기록하지 못함'); return null; }
        const buf = await subtle.digest('SHA-256', new TextEncoder().encode(String(text)));
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
        console.error('[가입] 서약 문안 해시 계산 실패:', e?.message || e);
        return null;
    }
}

/**
 * users.consents.vendorPledges 값 — 법무 §1-4 저장 구조
 * @param {{ termsV80Live: boolean, termsVersion: string, agreedAt: string }} p  termsVersion = 가입 시 consents.terms.version 과 같은 값
 */
export async function buildVendorPledges({ termsV80Live, termsVersion, agreedAt }) {
    const label = vendorPledgeLabel(termsV80Live);
    const textHash = await sha256Hex(label);
    const rec = {
        purge: true, noResub: true, onlyToLab: true,
        pledgeVersion: termsV80Live ? VENDOR_PLEDGE_VERSION_V80 : VENDOR_PLEDGE_VERSION_TRANSITIONAL,
        textHash: textHash || '',
        termsVersion: String(termsVersion || ''),
        agreedAt,
    };
    if (!textHash) rec.textHashFailed = true;
    return rec;
}

/** 개설등록번호·제조업 허가번호 메타 1건 — { type, number, uploadedAt } (path 없음). 번호는 trim 후 30자 */
export async function saveVendorNumberMeta(uid, type, number) {
    if (!VENDOR_NUMBER_META_TYPES[type]) throw new Error(`unknown vendor number meta type: ${type}`);
    const n = String(number || '').trim().slice(0, VENDOR_NUMBER_MAX);
    if (!n) throw new Error('empty vendor number');
    await setDoc(doc(collection(db, 'vendorDocs', uid, 'files')), { type, number: n, uploadedAt: serverTimestamp() });
}

/**
 * 가입 직후(uid 확보 뒤, users 문서 저장 전) 메타 일괄 쓰기. 절대 throw 하지 않는다.
 * @param {string} uid
 * @param {{ license?: {licenseNo:string, licenseName:string}, opening?: string, mfgPermit?: string }} m  값이 비면 그 항목은 건너뜀
 * @returns {Promise<{ ok: boolean, written: string[], failedTypes: string[] }>}
 */
export async function writeSignupVendorMetas(uid, m = {}) {
    const jobs = [];
    const lic = m.license || {};
    if (String(lic.licenseNo || '').trim()) {
        jobs.push(['license', () => saveVendorLicense(uid, String(lic.licenseNo).trim().slice(0, VENDOR_LICENSE_MAX), String(lic.licenseName || '').trim().slice(0, VENDOR_LICENSE_MAX))]);
    }
    if (String(m.opening || '').trim()) jobs.push(['opening', () => saveVendorNumberMeta(uid, 'opening', m.opening)]);
    if (String(m.mfgPermit || '').trim()) jobs.push(['mfg_permit', () => saveVendorNumberMeta(uid, 'mfg_permit', m.mfgPermit)]);

    const written = []; const failedTypes = [];
    for (const [type, run] of jobs) {
        try { await run(); written.push(type); }
        catch (e) {
            // 번호 문자열은 로그에 찍지 않는다(법무 §3-2) — type·오류 코드만
            console.error(`[가입] 외주 자격 메타 쓰기 실패 type=${type}:`, e?.code || e?.message || e);
            failedTypes.push(type);
        }
    }
    return { ok: failedTypes.length === 0, written, failedTypes };
}
